import React from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import {
  borderRadius,
  boxShadows,
  colors,
  fontFamily,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import CloseIcon from "@mui/icons-material/Close";

import InfoIcon from "./icons/InfoIcon";
import WarningIcon from "./icons/WarningIcon";
import SuccessIcon from "./icons/SuccessIcon";
import ErrorIcon from "./icons/ErrorIcon";

export type ToastType = "info" | "error" | "success" | "warning";

interface ToastProps {
  t: { id: string };
  type: ToastType;
  title?: string;
  message?: string | JSX.Element;
  onClose?: () => void;
}

// Fallback hex literals for tokens not present in this repo's defaultStyle.
const TOAST_GRADIENTS: Record<ToastType, string> = {
  info: `linear-gradient(180deg, ${colors.blue14} 0%, ${colors.blue16} 100%)`,
  success: `linear-gradient(180deg, ${colors.green} 0%, #006B47 100%)`,
  warning: `linear-gradient(180deg, ${colors.yellow} 0%, #B58800 100%)`,
  error: `linear-gradient(180deg, ${colors.red01} 0%, #A00000 100%)`,
};

const ToastContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: ${fontFamily.primary};
  z-index: 999;
  box-shadow: ${boxShadows.boxShadow07};
  border-radius: ${borderRadius.large};
`;

const ToastLeft = styled.div<{ type: ToastType }>`
  display: flex;
  width: 90px;
  padding: ${padding.medium2};
  justify-content: center;
  align-items: center;
  gap: ${gaps.small2};
  align-self: stretch;
  border-radius: ${borderRadius.large} ${borderRadius.zero} ${borderRadius.zero} ${borderRadius.large};
  background: ${(p) => TOAST_GRADIENTS[p.type]};
`;

const ToastRight = styled.div`
  display: flex;
  min-width: 428px;
  max-width: 428px;
  gap: ${gaps.medium1};
  padding: ${padding.xlarge1};
  justify-content: space-between;
  align-items: center;
  border-radius: ${borderRadius.zero} ${borderRadius.large} ${borderRadius.large} ${borderRadius.zero};
  background: #ffffff;
  cursor: pointer;
  position: relative;
`;

const ToastBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.xsmall2};
  width: 340px;
`;

const CloseIconRight = styled(CloseIcon)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;
`;

const ToastHeading = styled.h3`
  color: ${colors.black01};
  font-family: ${fontFamily.primary};
  font-size: ${fontSize.xlarge0};
  font-style: normal;
  font-weight: ${fontWeight.bold1};
  line-height: normal;
  margin: 0px;
`;

const ToastMessage = styled.p`
  color: ${colors.black01};
  font-family: ${fontFamily.primary};
  font-size: ${fontSize.medium};
  font-style: normal;
  font-weight: ${fontWeight.normal1};
  line-height: normal;
  margin: 0;
`;

const getToastIcon = (type: ToastType) => {
  switch (type) {
    case "error":
      return <ErrorIcon />;
    case "success":
      return <SuccessIcon />;
    case "warning":
      return <WarningIcon />;
    case "info":
      return <InfoIcon />;
    default:
      return null;
  }
};

const StewartToast: React.FC<ToastProps> = ({
  t,
  title,
  type,
  message,
  onClose,
}) => {
  const handleClose = () => {
    onClose && onClose();
    toast.remove(t.id);
  };

  return (
    <ToastContainer>
      <ToastLeft type={type}>{getToastIcon(type)}</ToastLeft>
      <ToastRight onClick={onClose}>
        <ToastBody>
          {title && <ToastHeading>{title}</ToastHeading>}
          {message && <ToastMessage>{message}</ToastMessage>}
        </ToastBody>
        <CloseIconRight onClick={handleClose} />
      </ToastRight>
    </ToastContainer>
  );
};

export default StewartToast;
